import { cn } from "../../utils/style";
import { formatCurrency } from "../../utils/helpers";
import { DailySalesStats, SalesStatsResult } from "../../dto/reports.dto";
import { LineChart } from "../tremor/LineChart";
import { useMemo } from "react";
import {
  differenceInDays,
  endOfWeek,
  format,
  formatISO,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  startOfWeek,
} from "date-fns";
import { EmptyState } from "./EmptyState";

interface RevenueCardProps {
  data: SalesStatsResult;
}

function aggregateWeekly(data: DailySalesStats[]) {
  const perWeek = new Map<string, { Product: number; Service: number }>();

  for (const v of data) {
    const sow = formatISO(startOfWeek(parseISO(v.date)), {
      representation: "date",
    });
    const current = perWeek.get(sow) ?? { Product: 0, Service: 0 };
    current.Product += parseFloat(v.revenue_product || "0");
    current.Service += parseFloat(v.revenue_service || "0");
    perWeek.set(sow, current);
  }

  const entries = Array.from(perWeek.entries());

  return entries.map(([start, value], i) => {
    const startDate = parseISO(start);
    const endDate = endOfWeek(startDate);
    const firstDate = parseISO(data[0].date);
    const lastDate = parseISO(data[data.length - 1].date);

    const adjustedStart =
      i === 0 && isBefore(startDate, firstDate) ? firstDate : startDate;
    const adjustedEnd =
      i === entries.length - 1 && isAfter(endDate, lastDate)
        ? lastDate
        : endDate;

    const title = isSameDay(adjustedStart, adjustedEnd)
      ? format(adjustedStart, "MMM dd")
      : `${format(adjustedStart, "MMM dd")} - ${format(adjustedEnd, "MMM dd")}`;

    return { date: title, ...value };
  });
}

export function RevenueCard({ data }: RevenueCardProps) {
  const chartData = useMemo(() => {
    const daily = data.daily_stats;
    if (daily.length === 0) return [];

    const first = daily[0].date;
    const last = daily[daily.length - 1].date;

    const dayCount = differenceInDays(last, first);

    if (dayCount > 60) {
      return aggregateWeekly(data.daily_stats);
    }

    return data.daily_stats.map((d) => ({
      date: format(d.date, "MMM dd"),
      Product: parseFloat(d.revenue_product || "0"),
      Service: parseFloat(d.revenue_service || "0"),
    }));
  }, [data]);

  return (
    <div
      className={cn(
        "transition bg-white p-8 rounded-md border border-gray-200",
      )}
    >
      <div className="flex items-center justify-between gap-x-2">
        <div className="flex items-center gap-x-2">
          <dt className="font-medium text-gray-900 sm:text-sm">
            Total Revenue
          </dt>
        </div>
      </div>
      <dd className="mt-2 text-2xl font-semibold text-gray-900">
        {formatCurrency(data.total_revenue || "0")}
      </dd>
      {chartData.length > 0 ? (
        <LineChart
          className="mt-6 h-32"
          data={chartData}
          index="date"
          colors={["indigo", "pink"]}
          categories={["Product", "Service"]}
          startEndOnly={true}
          valueFormatter={(value) => formatCurrency(value)}
          showYAxis={false}
          showLegend={false}
          showTooltip={true}
          autoMinValue
        />
      ) : (
        <EmptyState />
      )}
    </div>
  );
}
